import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import useLabels from "../../hooks/useLabels";
import { deleteProtocol, getProtocols } from "../../services/utils";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { selectWineMaking } from "../../context/selectors";
import SecondaryBar from "../SecondaryBar";
import { Button, ConfirmationModal, CTA } from "../Generic";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import TableEdit from "../TableEdit";
import TableCell from "../TableEdit/TableCell";
import { createColumnHelper } from "@tanstack/react-table";
import {
  removeWineMaking,
  setWineMaking,
} from "../../context/protocols/wineMakingSlice";
import useAuth from "../../hooks/useAuth";
import { ROLES } from "../../constants/base";
import { getItem } from "../../services/SessionStorage";
import { Label } from "../FormComponents";
import { toastAxios } from "../../services/toastAxios";
import { Icon } from "../../components/Icon";

const WineMakingProtocolDetail = () => {
  const { type: rawType, id } = useParams();
  const type = rawType?.toUpperCase();
  const wineMakingOps = useSelector(selectWineMaking);
  const [getLabel] = useLabels();
  const dispatch = useDispatch();
  const axiosPrivate = useAxiosPrivate();
  const [protocol, setProtocol] = useState(wineMakingOps?.find((dbProtocol) => dbProtocol.id === Number(id)) || {});
  const [operationsP, setOperationP] = useState(protocol?.operations || []);
  const [isOpen, setIsOpen] = useState(false);
  const { auth } = useAuth();
  const role = auth?.role || ROLES.GUEST;
  const navigate = useNavigate();

  const pageName = getLabel(
    type === "PHYSICAL" ? "wineMakingPhysicalProtocolTitlePage" :
    type === "PRE" ? "wineMakingProtocolTitlePage" :
    type === "POST" ? "wineMakingPostProtocolTitlePage" :
    "wineMakingCIPProtocolTitlePage", {
    name:
      wineMakingOps.find((dbProtocol) => dbProtocol.id === Number(id))?.name ||
      id,
  });

  const computeAddOpTypeString = (row) => {
    if (row?.treatments?.length > 1) {
      return getLabel(`operationType${row.type}`)
    }
    const protocol_type = row?.functional || null;
    const subtype = protocol_type ? getLabel(`drugLabelGroup_${protocol_type}`)?.toLowerCase?.() : null;
    return `${getLabel(`protocolType_${row.operation}`)} ${subtype ? ('(' + subtype + ')'):  ''}`
  }

  const columnHelper = createColumnHelper();
  const columns = [
      columnHelper.accessor(
        (row) => row?.day + (row?.day_to ? ` - ${row?.day_to}` : ""),
        {
          header: getLabel(type === "CIP" ? "protocolStep" : "protocolDay"),
          cell: TableCell,
          meta: {
            type: "number",
          },
        }
      ),
      ...(type !== "CIP" ? [columnHelper.accessor(
        (row) => (row?.phase ? getLabel(`protocolPhase_${row.phase}`) : ""),
        {
          header: getLabel("protocolPhase"),
          cell: TableCell,
          // meta: {
          //   type: "text",
          // },
          meta: {
            type: "select",
            options: [
              // { value: "Computer Science", label: "Computer Science" },
              // { value: "Communications", label: "Communications" },
              // { value: "Business", label: "Business" },
              // { value: "Psychology", label: "Psychology" },
            ],
          },
        }
      )] : []),
      columnHelper.accessor(
        (row) =>
          row?.operation ?
            row.operation === 'ADD' ? computeAddOpTypeString(row) : 
              getLabel(`protocolOperation_${row.operation}`) : "",
        {
          header: getLabel("protocolOperation"),
          cell: TableCell,
          meta: {
            type: "text",
          },
        }
      ),
      columnHelper.accessor(
        (row) =>
          row?.operation === "SO2"
            ? ""
            : (row.operation === "RINSE" || row.operation === "VAPORIZATION")
            ? getLabel("protocolProduct_H2O")
            : row?.product?.name || row?.product_name || "",
        {
          header: getLabel("protocolProduct"),
          cell: TableCell,
          meta: {
            type: "text",
          },
        }
      ),
      ...(type !== "CIP" ? [columnHelper.accessor(
        (row) =>
          row?.protocol_type ?
            row?.operation === 'ADD' ? "" :
            getLabel(`protocolType_${row?.protocol_type}`) +
              (row?.protocol_type === "HOMOGENIZATION_REASSEMBLY" &&
              row?.homogenization_type
                ? ` (${getLabel(
                    `protocolHomogenizationMass_${row?.homogenization_type}`
                  )?.toLowerCase?.()})`
                : "") +
              (row?.protocol_type === "CUSTOM"
                ? ` (${row?.oxygenation_dose_hours}h:${row?.oxygenation_dose_minutes}m)`
                : "")
            : "",
        {
          header: getLabel("protocolType"),
          cell: TableCell,
          meta: {
            type: "text",
          },
        }
      )] : []),
      columnHelper.accessor(
            (row) =>
              row?.quantity
                ? (row.operation === "RINSE" || row.operation === "VAPORIZATION")
                ? ""
                : row?.operation === "CLEANING"
                ? `${row?.quantity} %` :
                row?.protocol_type === "HOMOGENIZATION_TEMP"
                  ? `${row?.quantity} \u00b0C`
                  : (row?.quantity || 0) +
                    " " + (row?.operation === "OXYGENATION" ? getLabel("protocolOxygenationNoDose") : 
                    (row?.product?.unit || row?.product_unit || ""))
                : "",
            {
              header: getLabel(type !== 'CIP' ? "protocolQuantity" : "protocolConcentration"),
              cell: TableCell,
              meta: {
                type: "number",
              },
            }
          ),
      // columnHelper.accessor(
      //   (row) =>
      //     row?.quantity
      //       ? row.operation === "CLEANING"
      //         ? `${row?.quantity} %`
      //         : row?.operation === "SO2"
      //         ? `${row?.quantity} ${row?.product_unit}`
      //         : row?.protocol_type === "HOMOGENIZATION_TEMP"
      //         ? `${row?.quantity} \u00b0C`
      //         : `${row.quantity || 0} ${row?.operation === "OXYGENATION" ? "" :
      //             (row?.product?.unit || row?.product_unit || "")
      //           } ${
      //             row?.operation === "OXYGENATION"
      //               ? row?.protocol_type === "MICRO"
      //                 ? getLabel("protocolOxygenationNoDose_MICRO")
      //                 : row?.protocol_type === "MACRO"
      //                 ? getLabel("protocolOxygenationNoDose_MACRO")
      //                 : row?.protocol_type === "CUSTOM"
      //                 ? `${getLabel("protocolOxygenationNoDose_CUSTOM")}${
      //                     row?.oxygenation_dose_hours || ""
      //                   }h:${
      //                     row?.oxygenation_dose_minutes ||
      //                     ""
      //                   }m`
      //                 : ""
      //               : ""
      //           }`
      //       : "",
        // (row) =>
        //   row?.quantity
        //     ? row?.operation === "RINSE"
        //     ? ""
        //     : row?.operation === "CLEANING"
        //     ? `${row?.quantity} %` :
        //     row?.protocol_type === "HOMOGENIZATION_TEMP"
        //       ? `${row?.quantity} \u00b0C`
        //       : (row?.quantity || 0) +
        //         " " +
        //         (row?.product?.unit || row?.product_unit || "")
        //     : "",
      //   {
      //     header: getLabel(
      //       type === "CIP" ? "protocolConcentration" : "protocolQuantity"
      //     ),
      //     cell: TableCell,
      //     meta: {
      //       type: "number",
      //       register: modalRegister,
      //     },
      //   }
      // ),
      // columnHelper.accessor("oxygenationType", {
      //   header: getLabel("protocolOxygenationType"),
      //   cell: TableCell,
      //   meta: {
      //     type: "text",
      //     register: register,
      //   },
      // }),
    ];
  
    if (
      operationsP?.find((o) => o?.protocol_type === "HOMOGENIZATION_REASSEMBLY")
    ) {
      // columns.splice(
      //   6,
      //   0,
      columns.push(
        ...[
            
          // columnHelper.accessor(
          //   (row) =>
          //     row?.protocol_type && row?.protocol_type !== "ADD"
          //       ? row?.operation === "OXYGENATION"
          //         ? getLabel(`protocolOXYGENATION_${row?.protocol_type}`)
          //         : getLabel(`protocolType_${row?.protocol_type}`) +
          //           (row?.protocol_type === "HOMOGENIZATION_REASSEMBLY" &&
          //           row?.homogenization_type
          //             ? ` (${getLabel(
          //                 `protocolHomogenizationMass_${row?.homogenization_type}`
          //               )})`
          //             : "") +
          //           (row?.protocol_type === "CUSTOM"
          //             ? ` (${row?.oxygenation_dose_hours}h:${row?.oxygenation_dose_minutes}m)`
          //             : "")
          //       : "",
          //   {
          //     header: getLabel("protocolType"),
          //     cell: TableCell,
          //     meta: {
          //       type: "text",
          //       register: modalRegister,
          //       // placeholder: getLabel("lotColorPlaceholder"),
          //     },
          //   }
          // ),
          columnHelper.accessor(
            (row) =>
              row?.work_time &&
              (row?.pause_time || row?.homogenization_pause_time)
                ? `${row?.work_time} min/${
                    row?.pause_time || row?.homogenization_pause_time
                  }h ${
                    (type === "CIP" ||
                    row.operation === "HOMOGENIZATION_MASS") &&
                    row?.iter_per_day
                      ? `x ${row?.iter_per_day}`
                      : ""
                  }`
                : "",
            {
              header: getLabel("protocolWorkPauseTime"),
              cell: TableCell,
              meta: {
                type: "number",
              },
            }
          ),
          columnHelper.accessor(
            (row) =>
              row?.motor_speed
                ? row?.motor_speed + " %"
                : "",
            {
              header: getLabel("protocolMotorSpeed"),
              cell: TableCell,
              meta: {
                type: "number",
              },
            }
          ),
        ]
      );
    }
  
    if (type === "CIP") {
      columns.push(
        columnHelper.accessor(
          (row) =>
            row?.operation === "HOMOGENIZATION_MASS"
              ? ""
              : row?.work_time
              ? `${row?.work_time} min`
              : "",
          {
            header: getLabel("protocolHomogenizationContactTime"),
            cell: TableCell,
            meta: {
              type: "text",
            },
          }
        )
      );
    }
  
  
  // const columns = [
  //   ...(type !== 'PHYSICAL' ? ([columnHelper.accessor(
  //     (row) => row?.day + (row?.day_to ? ` - ${row?.day_to}` : ""),
  //     {
  //       header: getLabel("protocolDay"),
  //       cell: TableCell,
  //       meta: {
  //         type: "number",
  //       },
  //     }
  //   )]) : []),
  //   columnHelper.accessor(
  //     (row) => (row?.phase ? getLabel(`protocolPhase_${row.phase}`) : ""),
  //     {
  //       header: getLabel("protocolPhase"),
  //       cell: TableCell,
  //     }
  //   ),
  //   columnHelper.accessor(
  //     (row) =>
  //       row?.operation ? 
  //         row?.operation === "ADD" ? computeAddOpTypeString(row) :
  //         getLabel(`protocolOperation_${row.operation}`) : "",
  //     {
  //       header: getLabel(type === 'PHYSICAL' ? "protocolStabilizationMode" : "protocolOperation"),
  //       cell: TableCell,
  //       meta: {
  //         type: "text",
  //       },
  //     }
  //   ),
  //    ...(type === 'PHYSICAL' ? ([columnHelper.accessor(
  //     (row) => row?.temperature + (row?.temperature ? `${row?.temperature} °C` : ""),
  //     {
  //       header: getLabel("protocolTemperature"),
  //       cell: TableCell,
  //       meta: {
  //         type: "number",
  //       },
  //     }
  //   )]) : []),
  //   columnHelper.accessor(
  //     (row) => (row?.operation === "SO2" ? "" : row?.operation === "RINSE" ? getLabel("protocolProduct_H2O") : row?.product_name || ""),
  //     {
  //       header: getLabel("protocolProduct"),
  //       cell: TableCell,
  //       meta: {
  //         type: "text",
  //       },
  //     }
  //   ),
  //   columnHelper.accessor(
  //     (row) =>
  //       row?.quantity
  //         ? row?.operation === "RINSE"
  //         ? ""
  //         : row?.operation === "CLEANING"
  //         ? `${row?.quantity} %` :
  //         row?.protocol_type === "HOMOGENIZATION_TEMP"
  //           ? `${row?.quantity} \u00b0C`
  //           : (row?.quantity || 0) +
  //             " " + (row?.operation === "OXYGENATION" ? getLabel("protocolOxygenationNoDose") : 
  //             (row?.product?.unit || row?.product_unit || ""))
  //         : "",
  //     {
  //       header: getLabel(type !== 'CIP' ? "protocolQuantity" : "protocolConcentration"),
  //       cell: TableCell,
  //       meta: {
  //         type: "number",
  //       },
  //     }
  //   ),
  //   // columnHelper.accessor("cost", {
  //   //   header: getLabel("protocolCost"),
  //   //   cell: TableCell,
  //   //   meta: {
  //   //     type: "number",
  //   //   },
  //   // }),
  //   ...(type !== 'CIP' ? [
  //     // columnHelper.accessor("iterPerDay", {
  //     //   header: getLabel("protocolIterationPerDay"),
  //     //   cell: TableCell,
  //     //   meta: {
  //     //     type: "number",
  //     //   },
  //     // })
  //     ] : [
  //     columnHelper.accessor(
  //     (row) =>
  //       row?.operation === "HOMOGENIZATION_MASS"
  //       ? "" :
  //       row?.work_time
  //         ? `${row?.work_time} min`
  //         : "",
  //     {
  //       header: getLabel("protocolHomogenizationContactTime"),
  //       cell: TableCell,
  //       meta: {
  //         type: "number",
  //       },
  //     }
  //   ),]),
  //   // columnHelper.accessor("goal", {
  //   //   header: getLabel("protocolGoals"),
  //   //   cell: TableCell,
  //   // }),
  //   // columnHelper.accessor("note", {
  //   //   header: getLabel("protocolNotes"),
  //   //   cell: TableCell,
  //   // }),
  //   // columnHelper.accessor("oxygenationType", {
  //   //   header: getLabel("protocolOxygenationType"),
  //   //   cell: TableCell,
  //   //   meta: {
  //   //     type: "text",
  //   //     register: register,
  //   //   },
  //   // }),
  // ];

  // if (operationsP?.find(o => o?.protocol_type === 'HOMOGENIZATION_REASSEMBLY')) {
  //   columns.splice(5, 0, 
  //     columnHelper.accessor(
  //       (row) =>
  //         row?.protocol_type ?
  //           row?.operation === 'ADD' ? "" :
  //           getLabel(`protocolType_${row?.protocol_type}`) +
  //             (row?.protocol_type === "HOMOGENIZATION_REASSEMBLY" &&
  //             row?.homogenization_type
  //               ? ` (${getLabel(
  //                   `protocolHomogenizationMass_${row?.homogenization_type}`
  //                 )})`
  //               : "") +
  //             (row?.protocol_type === "CUSTOM"
  //               ? ` (${row?.oxygenation_dose_hours}h:${row?.oxygenation_dose_minutes}m)`
  //               : "")
  //           : "",
  //       {
  //         header: getLabel("protocolType"),
  //         cell: TableCell,
  //         meta: {
  //           type: "text",
  //         },
  //       }
  //     ),
  //     columnHelper.accessor(
  //         (row) =>
  //           row?.work_time &&
  //           (row?.pause_time || row?.homogenization_pause_time)
  //             ? `${row?.work_time} min/${
  //                 row?.pause_time || row?.homogenization_pause_time
  //               }h ${
  //                 (type === "CIP" ||
  //                 row.operation === "HOMOGENIZATION_MASS") &&
  //                 row?.iter_per_day
  //                   ? `x ${row?.iter_per_day}`
  //                   : ""
  //               }`
  //             : "",
  //       {
  //         header: getLabel("protocolWorkPauseTime"),
  //         cell: TableCell,
  //         meta: {
  //           type: "number",
  //         },
  //       }
  //     ),
  //     columnHelper.accessor(
  //       (row) =>
  //         row?.homogenization_motor_speed
  //           ? row?.homogenization_motor_speed + "%"
  //           : "",
  //       {
  //         header: getLabel("protocolMotorSpeed"),
  //         cell: TableCell,
  //         meta: {
  //           type: "number",
  //         },
  //       }
  //     )
  //   );
  // }

  const handleOpenModal = () => {
    setIsOpen(true);
  };

  const handleCloseModal = () => {
    setIsOpen(false);
  };

  const handleFormSubmit = async () => {
    await removeProtocol();
    handleCloseModal();
  };

  useEffect(() => {
    if (!wineMakingOps || wineMakingOps.length === 0) {
      loadProtocols();
    }
  }, []);

  useEffect(() => {
    if (wineMakingOps && wineMakingOps.length > 0) {
      const protocol = wineMakingOps?.find((dbProtocol) => dbProtocol.id === Number(id)) || {};
      setProtocol(protocol);

      setOperationP(
        protocol?.operations || []
      );
    }

  }, [wineMakingOps]);

  const loadProtocols = async () => {
    const currentProtocols = await getProtocols("winemaking", axiosPrivate);
    dispatch(setWineMaking(currentProtocols));
  };

  const customIdDeleteProtocol = "custom-id-delete-protocol";
  const removeProtocol = async () => {
    const response = await toastAxios(
      () => deleteProtocol(id, "winemaking", axiosPrivate),
      customIdDeleteProtocol,
      { name: operationsP?.name || "" },
      getLabel,
    );
    const requestPayload = {
      ...(id && { id: Number(id) }),
    };
    if (response && response?.success) {
      dispatch(removeWineMaking(requestPayload));
      navigate(`/protocols/treatment`);
      // navigate(`/protocols/winemaking/${type}`);
    }
  };

  const buildJson = () => {
    const data = [];
    operationsP?.forEach((row) => {
      const tmp = [
        row?.day,
        getLabel(`protocolPhase_${row?.phase}`),
        getLabel(`protocolOperation_${row?.operation}`),
        getLabel(`protocolType_${row?.protocolType}`),  //"TIPO OP."
        row?.product_name,
        row?.quantity,
        row?.product_unit,
        row?.cost,
        row?.type,
        row?.work_time,
        row?.pauseTime,
        row?.motor_speed,
        row?.iter_per_day,
        row?.oxygenation_dose_hours,
        row?.oxygenation_dose_minutes,
        row?.note,
        row?.goal,
      ];

      if (row?.protocolType === 'HOMOGENIZATION_TEMP') tmp[6] = '\u00b0C';
      if (row?.operation === 'SO2') tmp[4] = '';
      else if (row?.protocolType === 'HOMOGENIZATION_REASSEMBLY' && row?.homogenizationType) {
        tmp[8] = getLabel(`protocolHomogenizationMass_${row?.homogenizationType}`)
        tmp[10] = row?.homogenizationpause_time;
      } else if (row?.operation === 'OXYGENATION' && row?.protocolType) {
        tmp[8] = getLabel(`protocolOXYGENATION_${row?.protocolType}`)
      }
      data.push(tmp);
    });

    return data;
  };

  const handleExport = () => {
    const toastId = toast.loading(getLabel("toast_inProgress"), {
      type: toast.TYPE.INFO,
      position: toast.POSITION.BOTTOM_RIGHT,
      exclude: true,
      isLoading: true,
    });

    const data = buildJson();

    const language = getItem("i18LanguageLocale") || "it";
    const headers = [];
    switch (language) {
      case "en":
        headers.push(
          ...[
            "DAY",
            "PHASE",
            "OPERATION",
            "OP. TYPE",
            "OENOLOGICAL PRODUCT",
            "QUANTITY",
            "UNIT",
            "COST",
            "TYPE",
            "WORK TIME (min)",
            "PAUSE TIME (hh)",
            "MOTOR SPEED (% )",
            "NUMBER x DAY (hh)",
            "CUSTOM OXYG. HOURS (hh)",
            "CUSTOM OXIG. MINUTES (min)",
            "NOTE",
            "GOAL",
          ]
        );
        break;
      default:
        headers.push(
          ...[
            "GIORNO",
            "FASE",
            "OPERAZIONE",
            "TIPO OP.",
            "PRODOTTO ENOLOGICO",
            "QUANTITA'",
            "UNITA'",
            "COSTO",
            "TIPO",
            "TEMPO LAVORO (min)",
            "TEMPO PAUSA (hh)",
            "VELOCITA' MOTORE (%)",
            "NUMERO x GIORNO (hh)",
            "ORE OSSIG. PERSON. (hh)",
            "MINUTI OSSIG. PERSON. (min)",
            "NOTE",
            "OBIETTIVI",
          ]
        );
    }

    toast.update(toastId, {
      render:
        data?.length === 0
          ? getLabel("toast_exportWinemakingProtocolError")
          : getLabel("toast_exportWinemakingProtocolSuccess"),
      type: data?.length === 0 ? toast.TYPE.ERROR : toast.TYPE.SUCCESS,
      isLoading: false,
      position: toast.POSITION.BOTTOM_RIGHT,
      autoClose: 4000,
    });
    if (data?.length === 0) return;

    const worksheet = XLSX.utils.json_to_sheet(data);
    // var worksheet = XLSX.utils.aoa_to_sheet([
    //   [ '' ], // A1
    //   [ {t: "n", f: "A2+A3"}, {t: "s", f: 'CHOOSE("","red","blue","green")'} ] // A2
    // ]);

    const wscols = [
      { wch: 10 },
      { wch: 15 },
      { wch: 13 },
      { wch: 10 },
      { wch: 20 },
      { wch: 10 },
      { wch: 10 },
      { wch: 10 },
      { wch: 20 },
      { wch: 20 },
      { wch: 20 },
      { wch: 20 },
      { wch: 18 },
      { wch: 25 },
      { wch: 25 },
      { wch: 25 },
      { wch: 25 },
    ];
    worksheet["!cols"] = wscols;
    XLSX.utils.sheet_add_aoa(worksheet, [headers], { origin: "A1" });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    const protocolName = wineMakingOps.find((dbProtocol) => dbProtocol.id === Number(id))?.name || 'data';
    XLSX.writeFile(
      workbook,
      `${protocolName}.xlsx`,
      { compression: true }
    );
  };

  const renderNavMenu = () => {
    
    const navClickHandler = () => {
      navigate("/protocols");
      // const protocolType = type === 'PHYSICAL' ? 'treatment' : type;
      // const winemaking = type === 'PHYSICAL' ? '' : '/winemaking';
      // navigate(`/protocols${winemaking}/${protocolType}`);
    };

    return (
      <div className="primary_container_menu">
        <div className="primary_container_row">
          <Button arrowDirection="left" onClick={navClickHandler} />
          <h2>{pageName}</h2>
        </div>
        {role === ROLES.CLIENT && (
          <div className="primary_container_row">
            <NavLink to={`/protocols/winemaking/${type?.toLowerCase()}/${id}/update`}>
              <CTA>{getLabel("updateProtocol")}</CTA>
            </NavLink>

            <CTA onClick={handleOpenModal}>{getLabel("deleteProtocol")}</CTA>
              <NavLink to={`/protocols/winemaking/${type?.toLowerCase?.()}/${id}/duplicate`}>
              <CTA>
                {getLabel("wineMakingProtocolCopy")} <Icon icon="fa-clone" />
              </CTA>
            </NavLink>
            <CTA onClick={handleExport}>
              {getLabel("operationExcelExport")}{" "}
              <Icon icon="fa-file-arrow-down" />
            </CTA>
            <ConfirmationModal
              isOpen={isOpen}
              onConfirm={handleFormSubmit}
              onClose={handleCloseModal}
              description={getLabel("modalDeleteProtocolDescription", {
                name: protocol?.name || "",
              })}
            ></ConfirmationModal>
          </div>
        )}
      </div>
    );
  };

  const handleProgramNewOperation = () => {
    // save them in the localStorage
    localStorage.setItem(
      "selectedWinemakingProtocol",
      JSON.stringify({ type: type, id: id })
    );
    navigate(`/programming/winemaking/${type === 'POST' ? "post/" : ""}new`);
  }

  const renderNextOperation = () => {
    return (
      <div>
        <div className="primary_container_menu">
          <h4>{getLabel("protocol_nextOperations")}</h4>
          {/* <NavLink to={`/programming/${type === '' ? '' : 'post/'}new`}> */}
          <CTA onClick={handleProgramNewOperation}>{getLabel("programOperation")}</CTA>
          {/* </NavLink> */}
        </div>
      </div>
    );
  };

  return (
    <div className="primary_container">
      <SecondaryBar
        breadCrumb={[
          getLabel("protocolsNavLink"),
          getLabel("protocolNavTitlePage", {
            name: "",
            type: "",
          }),
        ]}
        isBasePathNeeded={false}
      />
      {renderNavMenu()}
      {type === 'CIP' && <div className="protocol_info_container_row">
          <Label
            label={getLabel("protocolSubtype_CLEANING")}
          >
            {protocol?.protocol_subtype ? getLabel(`protocolSubtype_${protocol?.protocol_subtype}`) : '-'}
          </Label>
      </div>}

      {type !== 'PHYSICAL' && <h4 className="">{getLabel("wineMakingProtocolSubstances")}</h4>}

      {type !== 'PHYSICAL' && <TableEdit
        defaultData={
          operationsP?.map((s) => {
            return {
              ...s,
              opType: "WINEMAKINGPROTOCOL",
            };
          }) || []
        }
        columns={columns}
        isSearchBar={false}
      />}
      {type === 'PHYSICAL' && <div>
        <div className="protocol_info_container_row">
          <Label
            label={getLabel("protocolSubtype")}
          >
            {getLabel(["STABILIZATION_STATIC", "STABILIZATION_DYNAMIC", "STABILIZATION_RESIN"].includes(operationsP?.[0]?.operation) ? `physicalProtocol_STABILIZATION` : '')}
          </Label>
          {operationsP?.[0]?.operation !== 'STABILIZATION_RESIN' && <Label
            label={getLabel("stabilizationType")}
          >
            {getLabel(operationsP?.[0]?.operation === 'STABILIZATION_RESIN' ? '`physicalProtocolSTABILIZATION_RESIN' : `physicalProtocol${operationsP?.[0]?.stabilization_type}`)}
          </Label>}
          <Label
            label={getLabel("protocolStabilizationType")}
          >
            {getLabel(`protocolOperation_${operationsP?.[0]?.operation}`)}
          </Label>
        </div>
        {!['STABILIZATION_RESIN'].includes(operationsP?.[0]?.operation) && <div className="protocol_info_container_row">
          <Label
            label={getLabel("stabilizationTemperature")}
          >
            {operationsP?.[0]?.temperature || "—"} {'\u00b0C'}
          </Label>
          {operationsP?.[0]?.operation === 'STABILIZATION_STATIC' && <Label
            label={getLabel("stabilizationWorkTime")}
          >
            {operationsP?.[0]?.work_time} {getLabel(Number(operationsP?.[0]?.work_time) > 1 ? "repeatSize_DAY" : "repeatSize_1DAY")?.toLowerCase?.()}
          </Label>}
        </div>}
        {operationsP?.[0]?.operation === 'STABILIZATION_RESIN' &&
        <div className="protocol_info_container_row">
          <Label
            label={getLabel("stabilizationPh")}
          >
            {operationsP?.[0]?.stabilization_ph || "—"}
          </Label>
          {operationsP?.[0]?.operation !== 'STABILIZATION_RESIN' && <Label
            label={getLabel("stabilizationQuantity")}
          >
            {operationsP?.[0]?.quantity || "—"}
          </Label>}
        </div>}
        {operationsP?.[0]?.operation === 'STABILIZATION_DYNAMIC' &&
        <div className="protocol_info_container_row">
          <Label
            label={getLabel("protocolStabilizationProduct")}
          >
            {operationsP?.[0]?.product_name || "—"}
          </Label>
          <Label
            label={getLabel("stabilizationQuantity")}
          >
            {operationsP?.[0]?.quantity || "—"} {operationsP?.[0]?.product_unit}
          </Label>
        </div>}
        {operationsP?.[0]?.operation === 'STABILIZATION_DYNAMIC' &&
        <div className="protocol_info_container_row">
          <Label
            label={getLabel("protocolWorkTime")}
          >
            {operationsP?.[0]?.work_time || "—"} {operationsP?.[0]?.work_time_unit || ''}
          </Label>
          <Label
            label={getLabel("protocolPauseTime")}
          >
            {operationsP?.[0]?.pause_time || "—"} {operationsP?.[0]?.pause_time_unit || ''}
          </Label>
          <Label
            label={getLabel("protocolMotorSpeed")}
          >
            {operationsP?.[0]?.motor_speed || "—"} %
          </Label>
          <Label
            label={getLabel("protocolHomogenizationIterPerDay")}
          >
            x{operationsP?.[0]?.iter_per_day}
          </Label>
        </div>}
      </div>}
      <div className="protocol_info_container_row">
        <Label
          width={400}
          height={150}
          label={getLabel("protocolNotes")}
          customClassName={"notes"}
        >
          {protocol?.notes}
        </Label>
      </div>

      {renderNextOperation()}
    </div>
  );
};

export default WineMakingProtocolDetail;
